import { Box } from '@mui/material'
import { brandColors, brandFonts } from '../styled/brand'
import { Wrap } from '../components/brand/Wrap'
import { Section, FeatureSection } from '../components/brand/Section'
import LogoMark from '../components/brand/LogoMark'
import Kicker from '../components/brand/Kicker'
import PhoneMockup from '../components/brand/PhoneMockup'
import StoreBadges from '../components/brand/StoreBadges'
import RingDecoration from '../components/brand/RingDecoration'

const screen = (name: string) => `${process.env.PUBLIC_URL}/home/${name}.png`

const trustItems = [
  {
    title: 'Nothing to back up',
    body: 'No backups needed, no phone storage used — everything lives securely in the cloud.',
  },
  {
    title: 'Any device, instantly',
    body: 'Install on any device, iOS or Android, and your data is already there — no setup, no syncing.',
  },
  {
    title: 'Photos, encrypted end-to-end',
    body: 'Encrypted on-device, so they stay inaccessible to anyone but you.',
  },
]

const labelChips = [
  { label: 'Garage', bg: brandColors.cream, color: '#3A2E1C', ml: 0 },
  { label: 'Office', bg: brandColors.creamLight, color: '#3A2A05', ml: 28 },
  { label: 'Guest Room', bg: brandColors.nearBlack2, color: brandColors.cream, ml: 12 },
  { label: 'Basement', bg: brandColors.cream, color: '#173034', ml: 44 },
]

const LabelChips = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      mt: '40px',
      alignItems: { xs: 'center', md: 'flex-start' },
    }}
  >
    {labelChips.map((chip) => (
      <Box
        key={chip.label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '12px 26px 12px 20px',
          background: chip.bg,
          color: chip.color,
          fontWeight: 700,
          fontSize: 16,
          borderRadius: '8px',
          width: 'fit-content',
          ml: { xs: 0, md: `${chip.ml}px` },
        }}
      >
        {chip.label}
      </Box>
    ))}
  </Box>
)

const Home = () => {
  return (
    <Box>
      {/* HERO */}
      <Section bg={brandColors.cream} py={0} sx={{ pt: '72px' }}>
        <RingDecoration
          size={820}
          color={brandColors.coral}
          opacity={0.08}
          sx={{ right: -260, top: -220 }}
        />
        <Wrap>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-start' },
              mb: '56px',
            }}
          >
            <LogoMark color={brandColors.nearBlack2} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '40px', md: '56px' },
              flexDirection: { xs: 'column', md: 'row' },
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            {/* Text */}
            <Box sx={{ flex: { xs: '0 1 auto', md: '0 1 440px' }, minWidth: 0, maxWidth: '100%' }}>
              <Kicker>Every belonging, every place</Kicker>
              <Box
                component="h1"
                sx={{
                  margin: '20px 0 0',
                  fontFamily: brandFonts.heading,
                  fontWeight: 700,
                  fontSize: { xs: 42, md: 60 },
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                  color: brandColors.nearBlack2,
                  textWrap: 'balance',
                }}
              >
                Never lose track of your{' '}
                <Box component="span" sx={{ color: brandColors.coralText }}>
                  stuff
                </Box>{' '}
                again.
              </Box>
              <Box
                component="p"
                sx={{
                  margin: '24px 0 0',
                  mx: { xs: 'auto', md: 0 },
                  fontSize: 19,
                  lineHeight: 1.5,
                  fontWeight: 500,
                  color: brandColors.textMuted,
                  maxWidth: 420,
                }}
              >
                Snap a photo of what you own, note where it lives, and find
                anything in seconds.
              </Box>
              <Box
                sx={{
                  mt: '40px',
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <StoreBadges height={64} />
              </Box>
            </Box>
            {/* Media — two overlapping phones */}
            <Box
              sx={{
                flex: { xs: '0 1 auto', md: '1 1 0' },
                minWidth: 0,
                display: 'flex',
                width: { xs: '100%', md: 'auto' },
                justifyContent: { xs: 'center', md: 'flex-end' },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '14px',
                  pt: '20px',
                  width: '100%',
                  maxWidth: 760,
                  minWidth: 0,
                }}
              >
                <PhoneMockup
                  src={screen('add-item')}
                  alt="Add an item"
                  width="100%"
                  maxWidth={340}
                  padding={9}
                  outerRadius={38}
                  innerRadius={28}
                  shadow="0 30px 70px rgba(31,29,27,0.28)"
                  transform="rotate(-6deg) translateY(14px)"
                  sx={{ flex: '1 1 0', minWidth: 0, zIndex: 1 }}
                />
                <PhoneMockup
                  src={screen('place-details')}
                  alt="Place details"
                  width="100%"
                  maxWidth={360}
                  padding={10}
                  outerRadius={40}
                  innerRadius={30}
                  shadow="0 30px 70px rgba(31,29,27,0.32)"
                  transform="rotate(5deg)"
                  sx={{ flex: '1 1 0', minWidth: 0, zIndex: 2 }}
                />
              </Box>
            </Box>
          </Box>
        </Wrap>
        <Box sx={{ height: 88 }} />
      </Section>

      {/* TRUST STRIP */}
      <Box
        sx={{
          background: brandColors.cream,
          borderTop: `1px solid ${brandColors.hairline}`,
          borderBottom: `1px solid ${brandColors.hairline}`,
          py: '48px',
        }}
      >
        <Wrap>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: '28px', md: '40px' },
              textAlign: 'center',
            }}
          >
            {trustItems.map((item) => (
              <Box key={item.title}>
                <Box
                  component="h3"
                  sx={{
                    margin: '0 0 8px',
                    fontFamily: brandFonts.heading,
                    fontWeight: 700,
                    fontSize: 16,
                    color: brandColors.nearBlack2,
                  }}
                >
                  {item.title}
                </Box>
                <Box
                  component="p"
                  sx={{
                    margin: 0,
                    fontSize: 15,
                    lineHeight: 1.5,
                    fontWeight: 500,
                    color: brandColors.textMuted,
                  }}
                >
                  {item.body}
                </Box>
              </Box>
            ))}
          </Box>
        </Wrap>
      </Box>

      {/* SMART SEARCH */}
      <FeatureSection
        variant="coral"
        kicker="Smart search"
        headline="Just describe it. We find it."
        body="Search by meaning, not keywords. No categories, no folders — type what you remember and the app understands."
        media={
          <PhoneMockup
            src={screen('semantic-search')}
            alt="Smart search"
            shadow="0 40px 100px rgba(42,19,16,0.34)"
          />
        }
      />

      {/* VISUAL SEARCH */}
      <FeatureSection
        variant="cream"
        reverse
        kicker="Visual search"
        headline="Search with a photo."
        body="Snap a picture and instantly surface similar items already in your inventory."
        media={
          <PhoneMockup
            src={screen('image-search')}
            alt="Visual search"
            shadow="0 40px 100px rgba(31,29,27,0.30)"
          />
        }
      />

      {/* LABELS */}
      <FeatureSection
        variant="coral"
        kicker="Organize your way"
        headline="Labels, not rigid folders."
        headlineSize={44}
        body="Tag places by room or purpose and group them how you actually think — no hierarchies to maintain."
        media={
          <PhoneMockup
            src={screen('label-management')}
            alt="Label management"
            shadow="0 40px 100px rgba(42,19,16,0.30)"
            transform="rotate(3deg)"
          />
        }
      >
        <LabelChips />
      </FeatureSection>

      {/* BROWSE */}
      <FeatureSection
        variant="cream"
        reverse
        kicker="Browse everything"
        headline="Your whole inventory."
        body="Every item you own, organized and searchable in one place — browse freely and assign multiple items to a place at once."
        media={
          <PhoneMockup
            src={screen('browse-items')}
            alt="Browse items"
            shadow="0 40px 100px rgba(31,29,27,0.30)"
          />
        }
      />

      {/* CTA */}
      <Section
        bg={brandColors.nearBlack}
        py={0}
        sx={{ pt: { xs: '72px', md: '120px' }, pb: { xs: '72px', md: '88px' } }}
      >
        <RingDecoration
          size={900}
          color={brandColors.coralAccentLight}
          opacity={0.07}
          innerR={30}
          strokeWidth={0.6}
          sx={{ left: '50%', top: -360, transform: 'translateX(-50%)' }}
        />
        <Wrap
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: '32px', md: '48px' },
            flexDirection: { xs: 'column', md: 'row' },
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Box
            component="h2"
            sx={{
              margin: 0,
              fontFamily: brandFonts.heading,
              fontWeight: 700,
              fontSize: { xs: 36, md: 48 },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: brandColors.cream,
            }}
          >
            Get started
            <br />
            <Box component="span" sx={{ color: brandColors.coralAccentLight }}>
              in seconds.
            </Box>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <StoreBadges height={56} gap={16} />
          </Box>
        </Wrap>
      </Section>
    </Box>
  )
}

export default Home
